import { Array } from "@siteimprove/alfa-array";
import { Element, Query } from "@siteimprove/alfa-dom";
import { Equatable } from "@siteimprove/alfa-equatable";
import { Iterable } from "@siteimprove/alfa-iterable";
import * as json from "@siteimprove/alfa-json";
import { Err, Ok, Result } from "@siteimprove/alfa-result";
import { Sequence } from "@siteimprove/alfa-sequence";
import { Page } from "@siteimprove/alfa-web";
import chalk from "chalk";
import { Audit } from "../audit/index.js";
import { getRuleTitle } from "./get-rule-title.js";
/**
 * Handling pretty-printing of console output.
 *
 * @public
 */
export class Logging {
    static of(title, severityOrLogs, logs) {
        const innerLogs = typeof severityOrLogs === "string" ? logs ?? [] : severityOrLogs ?? [];
        const severity = typeof severityOrLogs === "string" ? severityOrLogs : "log";
        return new Logging(title, Sequence.from(innerLogs), severity);
    }
    _title;
    _logs;
    _severity;
    constructor(title, logs, severity) {
        this._title = title;
        this._logs = logs;
        this._severity = severity;
    }
    get title() {
        return this._title;
    }
    get logs() {
        return this._logs;
    }
    print() {
        if (this._logs.isEmpty()) {
            console[this._severity](this._title);
        }
        else {
            console.group(this._title);
            this._logs.forEach((log) => log.print());
            console.groupEnd();
        }
    }
    equals(value) {
        return (value instanceof Logging &&
            value._title === this._title &&
            value._logs.equals(this._logs));
    }
    toJSON() {
        return {
            title: this._title,
            severity: this._severity,
            logs: this._logs.toJSON(),
        };
    }
}
/**
 * @public
 */
(function (Logging) {
    /** @internal */
    let Defaults;
    (function (Defaults) {
        Defaults.Title = "Untitled";
    })(Defaults = Logging.Defaults || (Logging.Defaults = {}));
    function isLogging(value) {
        return value instanceof Logging;
    }
    Logging.isLogging = isLogging;
    /**
     * @internal
     */
    function issueUrl(baseUrl, ruleId) {
        return chalk.underline(`${baseUrl}&conf=a+aa+aaa+aria+si&issue=cantTell+failed&wcag=twopointtwo#/${ruleId}/failed/`);
    }
    Logging.issueUrl = issueUrl;
    function fromIssue(baseUrl, ruleId) {
        return Logging.of(`Learn how to fix this issue: ${issueUrl(baseUrl, ruleId)}`);
    }
    /**
     * @internal
     */
    function errorTitle(n) {
        return ("The following problem" +
            (n === 1 ? " was " : "s were ") +
            "encountered while uploading results to the Siteimprove Intelligence Platform:");
    }
    Logging.errorTitle = errorTitle;
    /**
     * @internal
     */
    function fromAggregate(aggregate, pageTitle, pageReportUrl) {
        return Logging.of("Siteimprove found accessibility issues:", [
            // Show the page title
            Logging.of(chalk.bold(`Page - ${pageTitle ?? Defaults.Title}`)),
            // Show any error during upload: missing or invalid credentials, etc.
            ...(Err.isErr(pageReportUrl)
                ? [
                    Logging.of(errorTitle(pageReportUrl.getErr().length), pageReportUrl.getErr().map((error) => Logging.of(error, "warn"))),
                ]
                : []),
            // "This page contains X issues: URL" (if URL)
            // "This page contains X issues." (otherwise)
            Logging.of(`This page contains ${aggregate.length} issues${Result.isOk(pageReportUrl)
                ? ": " + chalk.underline(pageReportUrl.get())
                : "."}`, aggregate.map(([ruleId, { failed }], index) => 
            // n. Issue Title (X occurrences)
            Logging.of(`${index + 1}. ${getRuleTitle(ruleId)} (${failed} occurrence${failed > 1 ? "s" : ""})`, 
            // "Learn how to fix this issue: URL" (optional, if URL)
            typeof pageReportUrl === "string"
                ? [fromIssue(pageReportUrl, ruleId)]
                : pageReportUrl?.map((url) => fromIssue(url, ruleId))))),
        ]);
    }
    Logging.fromAggregate = fromAggregate;
    function fromAudit(audit, pageReportUrl, options) {
        // Retrieve or deserialize the page
        // We may waste a bit of time deserializing a page we won't need (if URL
        // and title are provided), but this streamlines error handling.
        const logs = (Page.isPage(audit.page) ? Ok.of(audit.page) : Page.from(audit.page)).map((page) => {
            const title = options?.pageTitle ??
                Query.getElementDescendants(page.document)
                    .filter(Element.isElement)
                    .find(Element.hasName("title"))
                    .map((title) => title.textContent())
                    .getOr(Defaults.Title);
            const pageTitle = typeof title === "string"
                ? title
                : title !== undefined
                    ? title(page)
                    : title;
            const filteredAggregates = Array.sortWith((Audit.isAudit(audit)
                ? audit.resultAggregates.toArray()
                : audit.resultAggregates).filter(([_, { failed }]) => failed > 0), ([uria], [urib]) => uria.localeCompare(urib)).map(([url, aggregate]) => [
                url.split("/").pop() ?? "",
                aggregate,
            ]);
            return fromAggregate(filteredAggregates, pageTitle, pageReportUrl);
        });
        return logs.getOrElse((error) => Logging.of(`Error, could not process the page: ${error}. Check the file format and try again. If the issue persists, contact support.`, "error"));
    }
    Logging.fromAudit = fromAudit;
})(Logging || (Logging = {}));
//# sourceMappingURL=logging.js.map